<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import DrillActionTile from './DrillActionTile.vue'
import SimpleCard from '@/components/SimpleCard.vue'
import CardsSelectionPanel from '@/components/CardsSelectionPanel.vue'
import UiPanel from '@/components/shared/UiPanel.vue'
import UiPanelWrapper from '@/components/shared/UiPanelWrapper.vue'

import {
  CommunityCardsCountMap,
  confirmCardsCount
} from '@/components/drills/helpers/confirm-cards-count'

import type { DrillAtomRound } from '@acepoker/contract'
import type { Card } from '@acepoker/contract/action'

const props = defineProps<{
  data: DrillAtomRound
  allCommunityActions: DrillAtomRound[]
  active?: boolean
}>()

const emit = defineEmits<{
  change: [tile: DrillAtomRound]
}>()

const countToSelect = computed(() => CommunityCardsCountMap[props.data.round])
const roundIndex = computed(() =>
  props.allCommunityActions.findIndex((el) => el.round === props.data.round)
)
const tableCards = computed(() =>
  (roundIndex.value > -1
    ? props.allCommunityActions.filter((el, i) => i < roundIndex.value)
    : props.allCommunityActions
  ).flatMap((el) => confirmCardsCount(CommunityCardsCountMap[el.round], splitCardsString(el.cards)))
)

const isSelectionOpened = ref(false)
const dataCards = ref(confirmCardsCount(countToSelect.value, splitCardsString(props.data.cards)))
watch(
  () => props.data.cards,
  (value) => (dataCards.value = confirmCardsCount(countToSelect.value, splitCardsString(value)))
)

function handleCardsChanged(cards: Card[]) {
  emit('change', {
    ...props.data,
    cards: joinCardsToString(cards)
  })
}

function closePanel(): void {
  isSelectionOpened.value = false
}

// helpers
function splitCardsString(value: string = ''): Card[] {
  return value.match(/.{1,2}/g)?.map((card) => ({ rank: card[0], suit: card[1] }) as Card) ?? []
}
function joinCardsToString(value: Card[] = []): string {
  return value
    .filter(Boolean)
    .map((card) => `${card.rank}${card.suit[0]}`)
    .join('')
}
</script>

<template>
  <drill-action-tile
    class="cursor-pointer"
    :active="props.active"
    @click="isSelectionOpened = true"
  >
    <template #header>
      <span class="uppercase"> {{ $t(`label.${data.round}`) }}</span>
    </template>

    <div class="my-0.5 flex justify-center gap-1">
      <simple-card
        v-for="(card, i) in dataCards"
        :key="i"
        :value="card ? card.rank + card.suit : null"
        :random="!card"
      />
    </div>

    <ui-panel-wrapper
      v-if="isSelectionOpened"
      :open="isSelectionOpened"
      @close="closePanel"
      modal
      drawer
      class="fixed!"
    >
      <ui-panel
        class="lg:w-auto!"
        :title="$t(`NewDrill.label.SelectCards_${data.round}`)"
        :onClose="closePanel"
      >
        <cards-selection-panel
          :cards="dataCards"
          @change="
            (e) => {
              handleCardsChanged(e)
              closePanel()
            }
          "
          :count="countToSelect"
          :tableCards="tableCards"
        />
      </ui-panel>
    </ui-panel-wrapper>

    <template #footer>
      <div class="text-3 text-center text-[13px]">{{ $t('NewDrill.label.EditCard') }}</div>
    </template>
  </drill-action-tile>
</template>

<style scoped></style>
